/**
 * Who is a signed-in, enrolled fan, and what it costs to find out.
 *
 * Written for the toast layer and still living in its folder, but no longer only its own: the site
 * header's loyalty links ask the same question (../navLink.js -- two anchors since 2026-08-21,
 * the mobile menu's row and the desktop navbar's button, behind ONE subscriber), the profile
 * blocks' non-member redirect asks it too (../nonMemberRedirect.js), and whenToastEligible() is
 * memoised so all three callers ride one chain run. Anything else that needs "is this fan
 * enrolled" belongs here too rather than in a second copy -- the answer costs an API read. (It used
 * to cost a session round trip as well; since D-063 the session is a cookie read.)
 *
 * Four gates, cheapest first, each one only asked when every cheaper gate has already said yes.
 * The ordering is the whole design: an anonymous visitor -- which is most of them, on every page of
 * the site -- costs one `await window.urcAuth.ready` and never builds an API client, never opens a
 * socket, never puts a node in the DOM.
 *
 * **The chain is terminal for every visitor**, including one who never signs in, and since
 * 2026-09-07 it is terminal *by construction* rather than by deadline. It used to wait out a 12s
 * cookie poll before it could settle a NO -- the `auth0_authenticated` cookie was written by an
 * inline `wp_footer` probe's callback and routinely arrived after this deferred bundle had read for
 * it, so a shorter wait reported "signed out" for fans who were not. `ready` settles once on every
 * branch, so the NO arrives in the same window as the yes.
 *
 * Nothing here is decided in PHP. The site is behind Varnish and the edge, so the server ships
 * identical markup to everyone and the browser derives the fan. App\Loyalty\SiteWideAssets decides
 * only whether the bundle is downloaded at all.
 *
 * **This chain fails CLOSED**, which is the deliberate opposite of the join strip
 * (joinStripReveal.js, whose INELIGIBLE list opens for every state it does not recognise). The
 * strip's reasoning is that a redundant invitation costs nothing, and that is true of a static
 * invitation. It is not true here: a fan who has not joined has no tier and no badges, so a
 * reconcile could only ever produce an empty list, while guessing yes buys a polling loop for the
 * life of every page they load. Worse, a not-enrolled fan's tier progress still resolves, so the
 * join transition itself can read as a promotion and fire a "you're now a Rookie" toast on top of
 * the success screen that already owns that moment.
 */

import { getSession } from '../session';
import { authState } from '../urcAuth';
import { isLoyaltyConsentDenied, isLoyaltyConsentGranted } from '../consent';
import { getClient } from '../client';
import { describeLoyaltyError } from '../errorDetail';
import { knownEnrolled, rememberEnrolled } from './enrolled';

/**
 * Verdicts. Everything except `armed` is terminal for the life of the page, and every one of them
 * is published to <html data-loyalty-toast> so the reason is legible without a debugger.
 *
 * `off`          -- no gateway configured; the feature is switched off, not failing.
 * `signed-out`   -- the SDK settled and nobody is signed in. Gate 1 never got past.
 * `no-session`   -- signed in, but no live loyalty token. D-046's split state.
 * `no-consent`   -- has a live token and has WITHHELD loyalty consent. A fan state, not a fault.
 * `consent-unknown` -- has a live token and the consent answer could not be READ: a Management API
 *                   outage, a misconfigured install, or the surface switched off by its config flag.
 *                   The consent half of `unknown`, and it exists for the same reason -- see below.
 * `not-enrolled` -- has not joined the programme.
 * `unconfigured` -- this tenant runs no loyalty programme.
 * `unknown`      -- the enrolment read failed. Closed, not open. See the file docblock.
 * `armed`        -- eligible.
 *
 * **`no-consent` was both of the first two**, from 2026-09-03 until this split. It closes every gate
 * either way, so nothing that merely reveals or hides ever needed to tell them apart -- but
 * ../nonMemberRedirect.js NAVIGATES, and it has to move a fan who declined while leaving a member
 * alone during an outage (D-062). Collapsed into one verdict it could do neither, so it did neither:
 * a signed-in fan who had not joined was sent nowhere at all, which is the bug this split fixes.
 */
export const VERDICT = {
	OFF: 'off',
	SIGNED_OUT: 'signed-out',
	NO_SESSION: 'no-session',
	NO_CONSENT: 'no-consent',
	CONSENT_UNKNOWN: 'consent-unknown',
	NOT_ENROLLED: 'not-enrolled',
	UNCONFIGURED: 'unconfigured',
	UNKNOWN: 'unknown',
	ARMED: 'armed',
};

/**
 * @return {boolean} Whether a gateway origin was configured for this environment.
 */
function hasGateway() {
	return Boolean( window.urcLoyalty && window.urcLoyalty.baseUrl );
}

/**
 * Gate 3. One read, one verdict -- deliberately no retry within the page.
 *
 * The fan is threaded through for the cache alone: `./enrolled.js` keys the remembered verdict on
 * the fan it was established for, so an account switch in this tab is a cache miss rather than an
 * inherited yes. Nothing else here needs it -- the client obtains the credential itself, before
 * every request.
 *
 * @param {string} fan The fan's Auth0 `sub`, from the settled AuthState.
 * @return {Promise<string>} A VERDICT.
 */
function askEnrolment( fan ) {
	if ( knownEnrolled( fan ) ) {
		return Promise.resolve( VERDICT.ARMED );
	}

	// First use of the client, and the first moment anything is sent to the gateway.
	return getClient()
		.getEnrolmentStatus()
		.then( status => {
			if ( status.state === 'enrolled' ) {
				rememberEnrolled( fan );

				return VERDICT.ARMED;
			}

			return status.state === 'unconfigured' ? VERDICT.UNCONFIGURED : VERDICT.NOT_ENROLLED;
		} )
		.catch( error => {
			// Not a fourth state and not a reason to guess. On a host whose origin is off the
			// gateway's CORS allowlist every read fails this way, and answering `armed` there would
			// start a polling loop that can never succeed, for every signed-in fan, on every page.
			console.warn( '[loyalty:toast] enrolment read failed', describeLoyaltyError( error ) );

			return VERDICT.UNKNOWN;
		} );
}

/**
 * The verdict once the chain has landed, `null` until then. Terminal for the life of the page.
 */
let settled = null;

/**
 * Callers still waiting for the one chain run, `null` when it has not started or has finished.
 *
 * @type {Array<(verdict: string) => void>|null}
 */
let waiting = null;

/**
 * Throw the verdict away, so the next subscriber runs the chain again.
 *
 * **For the account-switch reset, and for nothing else.** The memo holds ONE fan's verdict for the
 * life of the page and three subscribers ride it, so one fan replacing another inside a document
 * leaves the toast layer, the header's loyalty links and the profile blocks' non-member redirect
 * all holding the previous fan's answer. The redirect is the one that matters: an enrolled fan
 * switching to a not-enrolled one would not be bounced off a members-only page, which
 * `docs/subsystems/loyalty-toasts.md` records as the concrete harm the last time this chain went
 * stale.
 *
 * It deliberately does NOT re-run the chain. The subscribers each re-subscribe on their own terms
 * -- and re-running here would issue an enrolment read on every switch even on a page where
 * nothing is listening.
 *
 * A caller still waiting is left alone: `waiting` is untouched, so a chain currently in flight
 * settles normally for whoever asked. Its answer belongs to the fan it was started for, which is
 * why the reset is called BEFORE anything repaints -- see ../loyalty.jsx.
 *
 * @return {void}
 */
export function resetToastGate() {
	settled = null;
}

/**
 * Publish the verdict to everyone waiting, and to everyone who asks afterwards.
 *
 * @param {string} verdict A VERDICT.
 * @return {void}
 */
function settle( verdict ) {
	settled = verdict;

	const listeners = waiting ?? [];

	waiting = null;
	listeners.forEach( listener => listener( verdict ) );
}

/**
 * Subscribe to the loyalty gate chain. One chain per page, however many callers there are.
 *
 * The memo is not an optimisation. Two independent callers would mean two enrolment reads, on
 * every page, for every signed-in fan -- and an enrolment read is a real request, unlike gate 2.
 * Three callers exist today: the toast layer (toasts/index.js), the header's loyalty links
 * (../navLink.js, one subscriber for however many anchors it finds) and the profile blocks'
 * non-member redirect (../nonMemberRedirect.js, one subscriber and only on a page that carries the
 * field). Measured 2026-08-21 with the first two live: one /api/loyalty/status for the page.
 *
 * **The memo is per-page and holds one fan's verdict, which is a known residual.** An account
 * switch inside one document would otherwise leave all three subscribers holding the previous fan's
 * answer -- so `resetToastGate()` below is in ../loyalty.jsx's identity fan-out. The redirect is why
 * that matters: an enrolled fan switching to a not-enrolled one has to be bounced off a
 * members-only page. See docs/subsystems/loyalty-toasts.md.
 *
 * The name is historical -- this was the toast layer's private gate before the header needed the
 * same answer. `ARMED` means "signed in, has a live loyalty token, and has joined", which is the
 * question both callers are asking.
 *
 * @param {(verdict: string) => void} onVerdict Called exactly once, with a VERDICT. Never rejects.
 * @return {void}
 */
export function whenToastEligible( onVerdict ) {
	if ( settled !== null ) {
		onVerdict( settled );

		return;
	}

	if ( waiting !== null ) {
		waiting.push( onVerdict );

		return;
	}

	waiting = [ onVerdict ];

	runGates( settle );
}

/**
 * Run the gate chain and report one verdict.
 *
 * @param {(verdict: string) => void} onVerdict Called exactly once, with a VERDICT. Never rejects.
 * @return {void}
 */
function runGates( onVerdict ) {
	// Gate 0. Free, synchronous, and the difference between "the feature is off" and "every page
	// issues failing requests forever". LoyaltyConfig::apiUrl() is '' wherever
	// THREE_HALVES_BASE_URL is unset, and LoyaltyAPI joins its paths onto that -- so an empty
	// baseUrl aims every read at the WordPress origin. App\Loyalty\SiteWideAssets already declines
	// to load the bundle in that case; this is the browser-side half of the same guard, because a
	// page cached while the variable was set can outlive it being unset.
	if ( ! hasGateway() ) {
		onVerdict( VERDICT.OFF );

		return;
	}

	/*
	 * Gates 1 and 2, in one read. `getSession()` awaits `window.urcAuth.ready` and then asks the
	 * SDK for a loyalty bearer, so both questions are answered from one settle -- and BOTH verdicts
	 * survive the collapse, which they have to: `no-session` is what ../nonMemberRedirect.js
	 * redirects on and it is part of the `<html data-loyalty-toast>` vocabulary shared with
	 * `outlier-stats`. An AuthState carries no token, so `ready` alone cannot produce it.
	 *
	 * **What went, and why the faster NO is not a worse NO.** This used to be two gates with a 12s
	 * cookie poll in front of them. That window existed because the `auth0_authenticated` cookie
	 * was written by an inline `wp_footer` probe's *callback* and routinely arrived after this
	 * deferred bundle had already read for it -- so a definite "signed out" was only trustworthy
	 * after waiting the probe's whole timeout out, and the consent leg stacked a second window on
	 * top for a worst case around twenty-four seconds. Nothing arrives late now: `getTokenSilently`
	 * either answers or fails, and `ready` settles on every branch. The NO lands in the same window
	 * as the yes.
	 *
	 * **That matters most for the one subscriber that navigates.** ../nonMemberRedirect.js calls
	 * `window.location.replace()` on `no-session`, so this verdict now reaches it in about a second
	 * where it used to take twelve. It is the right answer sooner rather than a hastier one: without
	 * a loyalty bearer every self-scoped request on a profile page rejects before the wire, so there
	 * is no version of that page that works for this fan, and the join page they land on offers the
	 * sign-in that fixes it.
	 */
	getSession().then( session => {
		if ( ! session.token ) {
			const state = authState();

			// Two different answers, and telling them apart is the whole reason the verdict list
			// has both. `signed-out` is "the SDK settled and nobody is signed in", which is most of
			// the traffic and costs no request. `no-session` is D-046's split state -- the fan
			// resolved and the loyalty audience did not -- which ../../auth/runtime.js has already
			// warned about by the time this runs.
			onVerdict( state && state.signedIn ? VERDICT.NO_SESSION : VERDICT.SIGNED_OUT );

			return;
		}

		// Gate 2.5. Loyalty consent, and it is here rather than inside the API client because
		// this is the cheap early exit: a fan who has not consented never gets as far as
		// askEnrolment(), so the client is never built, no request is made, and the toast
		// layer never starts polling.
		//
		// ../consentGate.js is what actually ENFORCES this, one layer down and unbypassable.
		// This gate is the version that costs nothing, which is the same relationship gate 0
		// has with App\Loyalty\SiteWideAssets.
		//
		// Fails CLOSED, like every other gate in this chain, and for a stronger reason than
		// any of them: guessing yes means sending a fan's identity to a processor while the
		// record that says we may is the thing that failed to load. isLoyaltyConsentGranted()
		// never rejects, so an unreadable answer arrives here as `false`.
		isLoyaltyConsentGranted().then( granted => {
			if ( ! granted ) {
				// TWO verdicts out of one boolean. `isLoyaltyConsentGranted()` answers `false`
				// for a fan who DECLINED and for every way the answer can be UNREADABLE -- an
				// outage, a misconfigured install, the flag switched off, nobody signed in --
				// because the gate it was written for fails closed on all of them. One subscriber
				// on this chain navigates on that difference, so it has to survive to here.
				//
				// Read synchronously, which is sound because the consent machine announces its
				// terminal state BEFORE resolving -- see `lastState` in ../consent/machine.js.
				//
				// Both verdicts stay closed for every gate. The split changes who may be MOVED,
				// never who is let in.
				onVerdict(
					isLoyaltyConsentDenied() ? VERDICT.NO_CONSENT : VERDICT.CONSENT_UNKNOWN
				);

				return;
			}

			// The fan off the settled state, not decoded out of the bearer. Same value, from a
			// token the SDK validated rather than one this bundle split on a dot.
			const state = authState();

			askEnrolment( state && state.sub ? state.sub : '' ).then( onVerdict );
		} );
	} );
}
